import { Check } from 'lucide-react';
import {
  BENEFIT_OPTIONS,
  benefitHeadline,
  type BenefitType,
  type CreatorDraftPayload,
} from './draftSchema';

type Props = {
  payload: CreatorDraftPayload;
  onChange: (partial: Partial<CreatorDraftPayload>) => void;
};

const EXPIRY_OPTIONS: ReadonlyArray<{ days: number; label: string }> = [
  { days: 90, label: '3 months' },
  { days: 180, label: '6 months' },
  { days: 365, label: '12 months' },
  { days: 730, label: '24 months' },
  { days: 0, label: 'Never' },
];

const parseInteger = (raw: string): number | undefined => {
  const cleaned = raw.replace(/[^\d]/g, '');
  if (!cleaned) return undefined;
  const value = Number(cleaned);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
};

const parseRupeesToPaise = (raw: string): number | undefined => {
  const cleaned = raw.replace(/[^\d.]/g, '');
  if (!cleaned) return undefined;
  const value = Number(cleaned);
  return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) : undefined;
};

const formatRupeesWhole = (paise: number | undefined): string =>
  paise == null ? '' : String(Math.round(paise / 100));

const formatRupeesDecimal = (paise: number | undefined): string =>
  paise == null ? '' : (paise / 100).toFixed(2);

const StepBenefit = ({ payload, onChange }: Props) => {
  const benefitType = payload.benefitType;
  const discountMode = payload.discountMode ?? 'PERCENT';

  return (
    <div className="creator-step" data-testid="step-benefit">
      <header className="creator-step__header">
        <span className="creator-step__index">3</span>
        <div>
          <h2 className="creator-step__title">What&apos;s the main benefit?</h2>
          <p className="creator-step__subtitle">
            Pick how this membership rewards members. The card headline updates as you go.
          </p>
        </div>
      </header>

      <div className="creator-benefit-grid" role="radiogroup" aria-label="Benefit engine">
        {BENEFIT_OPTIONS.map((option) => {
          const isSelected = benefitType === option.type;
          return (
            <button
              key={option.type}
              type="button"
              className={`creator-benefit-card${isSelected ? ' creator-benefit-card--active' : ''}`}
              onClick={() => onChange({ benefitType: option.type as BenefitType })}
              data-testid={`creator-benefit-${option.type}`}
              aria-pressed={isSelected}
            >
              {isSelected ? (
                <span className="creator-benefit-card__check" aria-hidden="true">
                  <Check size={14} />
                </span>
              ) : null}
              <span className="creator-benefit-card__label">{option.label}</span>
              <span className="creator-benefit-card__caption">{option.caption}</span>
            </button>
          );
        })}
      </div>

      {/* ---- Per-engine config ---- */}
      {benefitType === 'DISCOUNT' ? (
        <div className="creator-benefit-config" data-testid="creator-benefit-discount">
          <div className="creator-segmented" role="tablist" aria-label="Discount type">
            <button
              type="button"
              className={`creator-segmented__btn${discountMode === 'PERCENT' ? ' creator-segmented__btn--active' : ''}`}
              onClick={() => onChange({ discountMode: 'PERCENT' })}
              data-testid="creator-discount-mode-percent"
            >
              % off
            </button>
            <button
              type="button"
              className={`creator-segmented__btn${discountMode === 'FLAT' ? ' creator-segmented__btn--active' : ''}`}
              onClick={() => onChange({ discountMode: 'FLAT' })}
              data-testid="creator-discount-mode-flat"
            >
              ₹ off
            </button>
          </div>

          {discountMode === 'FLAT' ? (
            <label className="creator-field">
              <span className="creator-field__label">FLAT AMOUNT OFF</span>
              <div className="creator-field__currency">
                <span className="creator-field__currency-symbol">₹</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="creator-field__input"
                  placeholder="100"
                  value={formatRupeesWhole(payload.discountFlatPaise)}
                  onChange={(event) => onChange({ discountFlatPaise: parseRupeesToPaise(event.target.value) })}
                  data-testid="creator-discount-flat"
                />
              </div>
              <span className="creator-field__hint">Rupees off every visit.</span>
            </label>
          ) : (
            <label className="creator-field">
              <span className="creator-field__label">DISCOUNT PERCENT</span>
              <div className="creator-field__currency">
                <input
                  type="text"
                  inputMode="numeric"
                  className="creator-field__input"
                  placeholder="15"
                  value={payload.discountPercent != null ? String(payload.discountPercent) : ''}
                  onChange={(event) => {
                    const next = parseInteger(event.target.value);
                    onChange({ discountPercent: next != null ? Math.min(next, 100) : undefined });
                  }}
                  data-testid="creator-discount-percent"
                />
                <span className="creator-field__currency-symbol">%</span>
              </div>
              <span className="creator-field__hint">Percent off everything (1–100).</span>
            </label>
          )}
        </div>
      ) : null}

      {benefitType === 'VISITS' ? (
        <div className="creator-benefit-config" data-testid="creator-benefit-visits">
          <label className="creator-field">
            <span className="creator-field__label">SESSIONS INCLUDED</span>
            <input
              type="text"
              inputMode="numeric"
              className="creator-field__input"
              placeholder="6"
              value={payload.includedVisits != null ? String(payload.includedVisits) : ''}
              onChange={(event) => onChange({ includedVisits: parseInteger(event.target.value) })}
              data-testid="creator-visits-count"
            />
            <span className="creator-field__hint">
              Number of sessions the member can use within the validity window.
            </span>
          </label>
        </div>
      ) : null}

      {benefitType === 'POINTS' ? (
        <div className="creator-benefit-config" data-testid="creator-benefit-points">
          <div className="creator-pricing-row">
            <label className="creator-field">
              <span className="creator-field__label">EARN RATE</span>
              <div className="creator-field__currency">
                <span className="creator-field__currency-symbol">₹</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="creator-field__input"
                  placeholder="100"
                  value={formatRupeesWhole(payload.pointsEarnRatePaisePerPoint)}
                  onChange={(event) =>
                    onChange({ pointsEarnRatePaisePerPoint: parseRupeesToPaise(event.target.value) })
                  }
                  data-testid="creator-points-earn-rate"
                />
              </div>
              <span className="creator-field__hint">Spend (₹) per 1 point earned.</span>
            </label>

            <label className="creator-field">
              <span className="creator-field__label">REDEEM VALUE</span>
              <div className="creator-field__currency">
                <span className="creator-field__currency-symbol">₹</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="creator-field__input"
                  placeholder="0.50"
                  value={formatRupeesDecimal(payload.pointsRedeemPaisePerPoint)}
                  onChange={(event) =>
                    onChange({ pointsRedeemPaisePerPoint: parseRupeesToPaise(event.target.value) })
                  }
                  data-testid="creator-points-redeem-value"
                />
              </div>
              <span className="creator-field__hint">Value of 1 point at checkout (₹).</span>
            </label>
          </div>

          <div className="creator-pricing-row">
            <label className="creator-field">
              <span className="creator-field__label">POINTS EXPIRE AFTER</span>
              <select
                className="creator-field__select"
                value={payload.pointsExpiryDays ?? ''}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  onChange({ pointsExpiryDays: Number.isFinite(next) ? next : undefined });
                }}
                data-testid="creator-points-expiry"
              >
                <option value="">Pick a window…</option>
                {EXPIRY_OPTIONS.map((option) => (
                  <option key={option.days} value={option.days}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="creator-field">
              <span className="creator-field__label">MIN REDEEM</span>
              <input
                type="text"
                inputMode="numeric"
                className="creator-field__input"
                placeholder="100"
                value={payload.pointsMinRedeem != null ? String(payload.pointsMinRedeem) : ''}
                onChange={(event) => onChange({ pointsMinRedeem: parseInteger(event.target.value) })}
                data-testid="creator-points-min-redeem"
              />
            </label>
          </div>
        </div>
      ) : null}

      {benefitType === 'PERKS_ONLY' ? (
        <div className="creator-benefit-config creator-points-disabled" data-testid="creator-benefit-perks-only">
          No reward engine — the perks you add next are the whole value of this membership.
        </div>
      ) : null}

      {benefitType ? (
        <div className="creator-points-example" data-testid="creator-benefit-headline">
          Card will read: <strong>{benefitHeadline(payload)}</strong>
        </div>
      ) : null}
    </div>
  );
};

export default StepBenefit;
